import { useEffect, useRef, useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { Check, Lock, X } from "lucide-react"

import { Avatar } from "@/components/tuw/Avatar"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { AcceptPriceNote, DeclineRequestConfirm, RequestFacts } from "@/components/tuw/openHouse/OpenHouseRequest"
import { postOrderAction } from "@/lib/orderActions"
import { BRIEF_WORDS, briefCopy, briefPagePath } from "@/lib/openHouse/brief"
import { requestCopy, requestsTitle } from "@/lib/openHouse/requests"

// The customer's review of freelancer requests: one card per waiting request
// with Accept/Decline. Opened from a Projects row and from the order page
// header. `from` tells the server where to return after the choice ("order"
// lands back on the order; omitted lands on the Projects list).
//
// On an order whose Open House brief is unfinished (`request.brief`), Accept
// shows what remains (phase-1-wording §7) instead of approving. An accept the
// server refused for that reason comes back as `flash.briefBlocked` (that
// request's id): the dialog then stays open on the same view, whether that
// flash reaches it on the visit the accept made or on the page it reopens on.
//
// An Open House request (`request.openHouse`) reads by role (new-flow-wording
// §13), shows its rating, date and any writer's price (§24), and asks before a
// decline (§8). Every other request reads and acts exactly as before.
export default function FreelancerRequestsDialog({ open, title, requests = [], from = null, onClose }) {
  const flashBlocked = usePage().props.flash?.briefBlocked ?? null
  const pendingBlocked = useRef(flashBlocked)
  const [blockedId, setBlockedId] = useState(null)
  const [decliningId, setDecliningId] = useState(null)
  const blocked = requests.find((request) => request.id === blockedId)
  const declining = requests.find((request) => request.id === decliningId)

  // A refusal that arrives while the dialog is closed waits for it to open.
  useEffect(() => {
    pendingBlocked.current = flashBlocked
  }, [flashBlocked])

  useEffect(() => {
    if (!open) {
      setDecliningId(null)
      return setBlockedId(null)
    }

    if (pendingBlocked.current) setBlockedId(pendingBlocked.current)
    pendingBlocked.current = null
  }, [open, flashBlocked])

  const act = (request, action) => {
    if (action === "approve" && request.brief && !request.brief.complete) return setBlockedId(request.id)

    const data = from ? { from } : {}
    let refused = false
    postOrderAction(request.orderId, `requests/${request.id}/${action}`, data, {
      onSuccess: (page) => {
        refused = action === "approve" && page?.props?.flash?.briefBlocked === request.id
      },
      onDone: () => (refused ? setBlockedId(request.id) : onClose()),
    })
  }

  if (declining) {
    return (
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="sm:max-w-[460px]">
          <DeclineRequestConfirm
            request={declining}
            onBack={() => setDecliningId(null)}
            onDecline={() => act(declining, "decline")}
          />
        </DialogContent>
      </Dialog>
    )
  }

  if (blocked) {
    return (
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="sm:max-w-[460px]">
          <BriefBlocked request={blocked} onBack={() => setBlockedId(null)} />
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[620px]">
        <DialogHeader>
          <DialogTitle className="font-display">{requestsTitle(requests, "Freelancer requests")}</DialogTitle>
          <DialogDescription>
            Review who wants to work on {title || "this project"}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {requests.map((request) => {
            const copy = request.openHouse ? requestCopy(request.brief?.kind) : null

            return (
              <div key={request.id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-start gap-3">
                  <Avatar
                    initials={request.freelancer.initials}
                    name={request.freelancer.name}
                    size={38}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-sm font-bold">{request.freelancer.name}</div>
                    <div className="mt-0.5 text-xs text-ink-3">
                      {copy ? copy.freelancer : request.role} · #{request.orderNumber} · {request.start}
                      {request.dueDays ? ` · ${request.dueDays} estimated days` : ""}
                    </div>
                  </div>
                  {request.expiresOn && (
                    <span className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-muted-foreground">
                      Expires {request.expiresOn}
                    </span>
                  )}
                </div>

                <RequestFacts request={request} />

                {request.note && (
                  <div className="mt-3 rounded-lg border border-border bg-accent px-3 py-2.5 text-[13px] leading-relaxed text-muted-foreground">
                    {request.note}
                  </div>
                )}
                {request.writingSample && (
                  <div className="mt-3">
                    <div className="mb-1 text-[11px] font-bold tracking-wide text-ink-3 uppercase">Writing sample</div>
                    <p className="max-h-36 overflow-y-auto rounded-lg border border-border bg-accent px-3 py-2.5 text-[13px] leading-relaxed text-muted-foreground">
                      {request.writingSample}
                    </p>
                  </div>
                )}

                <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                  <AcceptPriceNote request={request} />
                  <p className="mr-auto text-xs text-ink-3">{request.acceptanceNote}</p>
                  <Button
                    variant="outline"
                    className="gap-1.5"
                    disabled={!request.canDecline}
                    onClick={() => (copy ? setDecliningId(request.id) : act(request, "decline"))}
                  >
                    <X className="size-4" strokeWidth={2} />
                    {copy ? copy.decline : "Decline"}
                  </Button>
                  <Button
                    className="gap-1.5 font-semibold"
                    disabled={!request.canApprove}
                    onClick={() => act(request, "approve")}
                  >
                    <Check className="size-4" strokeWidth={2.2} />
                    Accept request
                  </Button>
                </div>
              </div>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// §7: the brief to finish before this request can be accepted.
function BriefBlocked({ request, onBack }) {
  const copy = briefCopy(request.brief?.kind)

  return (
    <>
      <DialogHeader>
        <span className="mb-1 flex size-9 items-center justify-center rounded-full bg-needs-you/15">
          <Lock className="size-4 text-needs-you" strokeWidth={2} />
        </span>
        <DialogTitle className="font-display">{copy.blockedHeading}</DialogTitle>
        <DialogDescription>{copy.blockedBody}</DialogDescription>
      </DialogHeader>

      <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
        <Avatar initials={request.freelancer.initials} name={request.freelancer.name} size={32} />
        <div className="min-w-0">
          <div className="font-display text-sm font-bold">{request.freelancer.name}</div>
          <div className="text-xs text-ink-3">{request.role}</div>
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-2 max-[560px]:flex-col-reverse max-[560px]:items-stretch">
        <Button variant="outline" onClick={onBack}>
          {BRIEF_WORDS.backToRequests}
        </Button>
        <Button className="font-semibold" onClick={() => router.visit(briefPagePath(request.orderId))}>
          {copy.cta}
        </Button>
      </div>
    </>
  )
}
